import React, { useState } from 'react';

interface FAQSectionProps {}

const FAQSection = ({}: FAQSectionProps) => {
  const [activeIndex, setActiveIndex] = useState<number>(0)

  const faqs = [
    {
      question: "What is AverPact?",
      answer:
        "AverPact is an enterprise milestone-based smart contract escrow platform. Instead of a bank or lawyer holding your money, it's locked in code on the blockchain. You create a contract with milestones, the client deposits funds, and when both sides agree a milestone is done - the money releases automatically. No middlemen.",
    },
    {
      question: "So nobody holds my money?",
      answer:
        "Correct. Funds sit in a smart contract on the blockchain - a non-custodial on-chain escrow. AverPact can't touch them. No bank can touch them. They only move when the conditions you agreed on are met. The smart contract holds funds, enforces rules, and auto releases.",
    },
    {
      question: "What if something goes wrong?",
      answer:
        "Either party can open a dispute. A neutral Arbitrator (assigned by the Client) reviews the on-chain evidence - documents, approvals, timestamps - and resolves it with a configurable fund split (e.g. 60/40). Transparent and fair.",
    },

    {
      question: "Which blockchains do you support?",
      answer:
        "Ethereum, Polygon, TRON, BSC, and more. Solidity smart contracts deployed across all supported chains. Non-custodial escrow with on-chain audit trail on every network.",
    },
    {
      question: "Is AverPact a replacement for traditional financial instruments?",
      answer:
        "Yes. AverPact is designed to replace traditional instruments such as letters of credit, manager's checks, and escrow accounts. Instead of relying on banks or intermediaries, transactions are secured through smart contracts on the blockchain. This reduces delays, lowers costs, and removes counterparty risk while maintaining the same level of trust and security.",
    },
    {
      question: "How does AverPact digitalize the payment process?",
      answer:
        "AverPact fully digitalizes the entire transaction lifecycle. Every document, attachment, and action within a deal is securely recorded on the blockchain, creating a permanent and traceable digital signature. This means all parties can monitor the progress of the transaction in real time, access all related documents, and verify every step of the process - ensuring complete transparency, security, and an immutable record that remains accessible forever.",
    },
  ]

  return (
    <section id="faq" className="w-full bg-white py-12 sm:py-16 lg:py-20">
      <div className="w-full max-w-[1200px] mx-auto px-4 lg:px-8">
        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-12 w-full">
          {/* Left Side - Content */}
          <div className="w-full lg:w-[40%] flex flex-col items-start gap-6 lg:sticky lg:top-8">
            <img 
              src="/images/img_image.jpg" 
              alt="Support" 
              className="w-full max-w-[400px]"
            />

            <div className="flex flex-col items-center lg:items-start gap-3 w-full">
              <h2 className="text-[22px] sm:text-[28px] md:text-[32px] font-semibold leading-snug text-center lg:text-left text-[#141A23] font-poly">
                Common questions
              </h2>
              
              <p className="text-sm sm:text-base font-normal leading-relaxed text-center lg:text-left text-[#141A237f] font-dm w-full">
                The short version of everything you need to know.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 w-full">
              <span className="text-sm sm:text-base font-semibold leading-[21px] text-left text-[#141A237f] font-dm">
                Still have questions?
              </span>
              <div className="flex items-center px-1.5">
                <a 
                  href="mailto:hello@averpact.com"
                  className="text-base font-semibold leading-[21px] text-left bg-[linear-gradient(180deg,#FF3333_0%,#FF3333_100%)] bg-clip-text text-transparent font-dm cursor-pointer hover:opacity-80"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>

          {/* Right Side - FAQ */}
          <div className="w-full lg:w-[60%] flex flex-col items-center gap-3">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className={`w-full rounded-xl p-4 sm:p-5 transition-all duration-300 cursor-pointer ${
                  activeIndex === index 
                    ? 'border-2 border-[#FF3333] bg-[#FF33330a]' 
                    : 'border border-[#141A231e] hover:border-[#141A233f]'
                }`}
                onClick={() => setActiveIndex(activeIndex === index ? -1 : index)}
              >
                <div className="flex justify-between items-center w-full">
                  <h3 className="text-sm sm:text-base font-semibold leading-snug text-left text-[#141A23] font-poly pr-4">
                    {faq.question}
                  </h3>
                  <button className="ml-4 flex-shrink-0 w-6 h-6 flex items-center justify-center">
                    <svg 
                      className={`w-4 h-4 transition-transform duration-300 ${activeIndex === index ? 'rotate-45' : ''}`} 
                      fill="none" 
                      stroke="#141A23" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
                
                {activeIndex === index && (
                  <div className="mt-3">
                    <p className="text-sm sm:text-base font-normal leading-relaxed text-left text-[#141A237f] font-dm">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default FAQSection
